import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ComponentType,
	type ReactNode,
} from 'react';
import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';
import { usePostOfficeContext } from '@atlassian/post-office-context';
import {
	type AdditionalMetadata,
	MessageInstanceEventClient,
	useMessageContext,
	usePlacementContext,
	useRecommendationContext,
} from '@atlassian/post-office-context/message-lifecycle';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { useIsAdmin as useIsTenantContextAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import {
	useMessageInstances,
	type UseMessageInstancesOptions,
} from '@atlassian/post-office-headless-api/hooks/useMessageInstances';
import { JiraSpotlight } from '@atlassian/jira-spotlight/src/ui/jira-spotlight.tsx';
import { getDefaultOnboardingSpotlightProps } from './onboardingConstants.tsx';
import { usePremiumFlowResolvedPromise } from './controllers/selectors/premium-hooks.tsx';
import { useIsVisibleWhenNoModalOnScreen } from './useIsVisibleWhenNoModalOnScreen.tsx';
import { ConditionalSpotlightTransition } from './ui/ConditionalSpotlightTransition.tsx';

type HeadlessMessageContext = {
	featureId?: string;
	messageType?: string;
	messageId?: string;
};

type HeadlessMessage = {
	messageInstanceId: string;
	messageTemplateId: string;
	messageCreationType?: 'implicit' | 'explicit';
	triggerId?: string;
	messageType?: string;
	messageId?: string;
	context: HeadlessMessageContext;
};

type HeadlessSpotlightMessageMetadata = {
	messageTemplateId?: string;
	featureId?: string;
	messageType?: string;
	messageId?: string;
};

type HeadlessSpotlightDismissKind = 'dismiss' | 'primary';

type HeadlessSpotlightClickIdentifiers = {
	dismiss: string;
	primary: string;
};

type UsePolarisHeadlessSpotlightMessageParams = {
	placementId: string;
	messageTemplateId: string;
	featureId: string;
	legacyDismissed?: boolean;
	clickIdentifiers?: HeadlessSpotlightClickIdentifiers;
};

type UsePolarisHeadlessSpotlightMessageResult = {
	isPoMessageVisible: boolean;
	messageInstanceId?: string;
	messageMetadata?: HeadlessSpotlightMessageMetadata;
	PostOfficeMessageInstance?: ComponentType<{
		messageInstanceId: string;
		children: () => ReactNode;
	}>;
	dismiss: (kind: HeadlessSpotlightDismissKind) => void;
	renderJiraSpotlight: (params: HeadlessJiraSpotlightRenderParams) => React.JSX.Element | null;
};

export type HeadlessJiraSpotlightRenderParams = Omit<
	React.ComponentProps<typeof JiraSpotlight>,
	'messageId' | 'messageType'
> & {
	fallbackMessageId: string;
	messageType?: React.ComponentProps<typeof JiraSpotlight>['messageType'];
	shouldUseTransition?: boolean;
	key?: React.JSX.IntrinsicAttributes['key'];
	isVisible?: boolean;
};

const DEFAULT_CLICK_IDENTIFIERS: HeadlessSpotlightClickIdentifiers = {
	dismiss: 'dismiss',
	primary: 'primary-cta',
};
const DISMISS_TRACKING_LOG_LOCATION =
	'polaris.lib-onboarding-flows.headless-spotlight-message.dismiss-tracking';
const WARN_LOG_SAMPLE_RATE = 0.02;

const useMatchByFeatureId = (
	featureId: string,
): NonNullable<UseMessageInstancesOptions<HeadlessMessage, Record<never, never>>['match']> =>
	useCallback(
		({ matchBuilder }) =>
			matchBuilder.messageContext(
				(context: HeadlessMessageContext | undefined) => context?.featureId === featureId,
			),
		[featureId],
	);

const useMessageMetadata = (
	message: HeadlessMessage | undefined,
): HeadlessSpotlightMessageMetadata | undefined =>
	useMemo(
		() =>
			message
				? {
						messageTemplateId: message.messageTemplateId,
						featureId: message.context?.featureId,
						// Message type/id can come either at message root or inside context depending on producer.
						messageType: message.messageType ?? message.context?.messageType,
						messageId: message.messageId ?? message.context?.messageId,
					}
				: undefined,
		[message],
	);

type UseSendDismissTrackingEventParams = {
	clickIdentifiers: HeadlessSpotlightClickIdentifiers;
	isInsideMessageLifecycleContext: boolean;
	messageInstanceClicked: ReturnType<typeof useMessageInstanceEvent>['messageInstanceClicked'];
	messageInstanceEventClient: MessageInstanceEventClient;
	message: HeadlessMessage | undefined;
	messageTemplateId: string;
	placementId: string;
	postOfficeContext: ReturnType<typeof usePostOfficeContext>;
	recommendationContext: ReturnType<typeof useRecommendationContext>;
	additionalMetadataFromFrontend: AdditionalMetadata;
};

const useSendDismissTrackingEvent = ({
	clickIdentifiers,
	isInsideMessageLifecycleContext,
	messageInstanceClicked,
	messageInstanceEventClient,
	message,
	messageTemplateId,
	placementId,
	postOfficeContext,
	recommendationContext,
	additionalMetadataFromFrontend,
}: UseSendDismissTrackingEventParams) => {
	const shouldLogSampledWarn = useCallback(() => Math.random() < WARN_LOG_SAMPLE_RATE, []);
	const logDismissTrackingFailure = useCallback(
		(source: 'messageInstanceClicked' | 'messageInstanceEventClient', error?: unknown) => {
			if (!shouldLogSampledWarn()) {
				return;
			}

			const logMessage = `Failed to send dismiss tracking event via ${source}`;
			if (error instanceof Error) {
				log.safeWarnWithoutCustomerData(DISMISS_TRACKING_LOG_LOCATION, logMessage, error);
				return;
			}
			log.safeWarnWithoutCustomerData(DISMISS_TRACKING_LOG_LOCATION, logMessage);
		},
		[shouldLogSampledWarn],
	);

	const buildClickedElement = useCallback(
		(
			kind: HeadlessSpotlightDismissKind,
		): {
			clickIdentifier: string;
			clickedElement: 'button';
			buttonType: 'primary' | 'secondary';
		} => ({
			clickIdentifier: kind === 'primary' ? clickIdentifiers.primary : clickIdentifiers.dismiss,
			clickedElement: 'button',
			buttonType: kind === 'primary' ? 'primary' : 'secondary',
		}),
		[clickIdentifiers.primary, clickIdentifiers.dismiss],
	);

	return useCallback(
		(kind: HeadlessSpotlightDismissKind, currentMessageInstanceId: string) => {
			const clickedElement = buildClickedElement(kind);
			if (isInsideMessageLifecycleContext) {
				messageInstanceClicked({
					messageInstanceId: currentMessageInstanceId,
					messageTemplateId: message?.messageTemplateId,
					messageCreationType: message?.messageCreationType,
					messageTriggerId: message?.triggerId,
					messageEventType: 'clicked',
					clickedElement,
					action: {
						actionType: 'dismiss',
					},
					additionalMetadataFromFrontend,
				}).catch((error: unknown) => {
					logDismissTrackingFailure('messageInstanceClicked', error);
				});
				return;
			}

			messageInstanceEventClient
				.sendMessageInstanceEvent({
					messageEventType: 'clicked',
					messageInstanceId: currentMessageInstanceId,
					messageTemplateId: message?.messageTemplateId ?? messageTemplateId,
					deliveryChannel: 'in-app',
					placementId,
					channelType: 'pull',
					context: {
						...postOfficeContext,
						source: placementId,
						recommendation: {
							recommendationSessionId: recommendationContext.sessionId ?? '',
							recommendationEntityId: recommendationContext.entityId ?? '',
						},
					},
					messageCreationType: message?.messageCreationType ?? 'implicit',
					messageTriggerId: message?.triggerId ?? '',
					timestamp: Date.now(),
					clickedElement,
					action: {
						actionType: 'dismiss',
					},
					additionalMetadataFromFrontend,
				})
				.catch((error: unknown) => {
					logDismissTrackingFailure('messageInstanceEventClient', error);
				});
		},
		[
			additionalMetadataFromFrontend,
			buildClickedElement,
			isInsideMessageLifecycleContext,
			logDismissTrackingFailure,
			messageInstanceClicked,
			messageInstanceEventClient,
			message?.messageTemplateId,
			message?.messageCreationType,
			message?.triggerId,
			messageTemplateId,
			placementId,
			postOfficeContext,
			recommendationContext.entityId,
			recommendationContext.sessionId,
		],
	);
};

type UseSyncLegacyDismissParams = {
	legacyDismissed?: boolean;
	messageInstanceId?: string;
	dismissedIds: ReadonlySet<string>;
	sendDismissTrackingEvent: (
		kind: HeadlessSpotlightDismissKind,
		currentMessageInstanceId: string,
	) => void;
	dismissMessage: (messageInstanceId: string) => void;
};

const useSyncLegacyDismiss = ({
	legacyDismissed,
	messageInstanceId,
	dismissedIds,
	sendDismissTrackingEvent,
	dismissMessage,
}: UseSyncLegacyDismissParams): void => {
	const syncedLegacyDismissMessageInstanceIdRef = useRef<string | undefined>(undefined);

	useEffect(() => {
		if (
			legacyDismissed === true &&
			messageInstanceId &&
			!dismissedIds.has(messageInstanceId) &&
			syncedLegacyDismissMessageInstanceIdRef.current !== messageInstanceId
		) {
			sendDismissTrackingEvent('dismiss', messageInstanceId);
			dismissMessage(messageInstanceId);
			syncedLegacyDismissMessageInstanceIdRef.current = messageInstanceId;
		}
	}, [legacyDismissed, messageInstanceId, dismissedIds, sendDismissTrackingEvent, dismissMessage]);
};

export const usePolarisHeadlessSpotlightMessage = ({
	placementId,
	messageTemplateId,
	featureId,
	legacyDismissed,
	clickIdentifiers = DEFAULT_CLICK_IDENTIFIERS,
}: UsePolarisHeadlessSpotlightMessageParams): UsePolarisHeadlessSpotlightMessageResult => {
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const postOfficeContext = usePostOfficeContext();
	const recommendationContext = useRecommendationContext();
	const messageContext = useMessageContext();
	const placementContext = usePlacementContext();
	const isAdmin = useIsTenantContextAdmin();
	const premiumFlowResolvedPromise = usePremiumFlowResolvedPromise();
	const messageInstanceEventClient = useMemo(() => new MessageInstanceEventClient(), []);
	const [isPremiumFlowResolved, setIsPremiumFlowResolved] = useState(false);

	useEffect(() => {
		let active = true;
		premiumFlowResolvedPromise.then(() => {
			if (active) {
				setIsPremiumFlowResolved(true);
			}
		});

		return () => {
			active = false;
		};
	}, [premiumFlowResolvedPromise]);

	const matchByFeatureId = useMatchByFeatureId(featureId);
	const { messages, dismissedIds, dismissMessage, PostOfficeMessageInstance, isLoading } =
		useMessageInstances<HeadlessMessage>(
			{
				placementId,
				messageTemplateId,
			},
			{
				match: matchByFeatureId,
			},
		);

	const message = messages[0];
	const messageInstanceId = message?.messageInstanceId;
	const messageMetadata = useMessageMetadata(message);
	const isVisibleWhenNoModalOnScreen = useIsVisibleWhenNoModalOnScreen();

	const isPoMessageVisible = Boolean(
		isPremiumFlowResolved &&
		messageInstanceId &&
		!dismissedIds.has(messageInstanceId) &&
		isLoading === false &&
		isVisibleWhenNoModalOnScreen,
	);
	const isInsideMessageLifecycleContext = Boolean(
		messageContext.messageInstanceId &&
		messageContext.messageTemplateId &&
		placementContext.placementId &&
		messageContext.messageCreationType &&
		messageContext.triggerId,
	);
	const additionalMetadataFromFrontend: AdditionalMetadata = useMemo(
		() => ({
			isSiteAdmin: Boolean(postOfficeContext.isSiteAdmin),
			isAdmin,
			hasAdminRole: isAdmin || Boolean(postOfficeContext.isSiteAdmin),
		}),
		[isAdmin, postOfficeContext.isSiteAdmin],
	);
	const sendDismissTrackingEvent = useSendDismissTrackingEvent({
		clickIdentifiers,
		isInsideMessageLifecycleContext,
		messageInstanceClicked,
		messageInstanceEventClient,
		message,
		messageTemplateId,
		placementId,
		postOfficeContext,
		recommendationContext,
		additionalMetadataFromFrontend,
	});

	useSyncLegacyDismiss({
		legacyDismissed,
		messageInstanceId,
		dismissedIds,
		sendDismissTrackingEvent,
		dismissMessage,
	});

	const dismiss = useCallback(
		(kind: HeadlessSpotlightDismissKind) => {
			if (!messageInstanceId) {
				return;
			}

			sendDismissTrackingEvent(kind, messageInstanceId);
			dismissMessage(messageInstanceId);
		},
		[messageInstanceId, sendDismissTrackingEvent, dismissMessage],
	);
	const defaultSpotlightProps = useMemo(() => getDefaultOnboardingSpotlightProps(), []);

	const renderJiraSpotlight = useCallback(
		({
			fallbackMessageId,
			shouldUseTransition = true,
			key: spotlightKey,
			isVisible: isSpotlightVisible = true,
			...spotlightProps
		}: HeadlessJiraSpotlightRenderParams): React.JSX.Element | null => {
			if (!isPoMessageVisible || !isSpotlightVisible) {
				return null;
			}

			const spotlight = (
				<JiraSpotlight
					key={spotlightKey}
					{...defaultSpotlightProps}
					{...spotlightProps}
					messageId={messageMetadata?.messageTemplateId ?? fallbackMessageId}
				/>
			);

			const spotlightWithTransition = shouldUseTransition ? (
				<ConditionalSpotlightTransition>{spotlight}</ConditionalSpotlightTransition>
			) : (
				spotlight
			);

			if (PostOfficeMessageInstance && messageInstanceId) {
				return (
					<PostOfficeMessageInstance messageInstanceId={messageInstanceId}>
						{() => spotlightWithTransition}
					</PostOfficeMessageInstance>
				);
			}

			return spotlightWithTransition;
		},
		[
			PostOfficeMessageInstance,
			defaultSpotlightProps,
			isPoMessageVisible,
			messageInstanceId,
			messageMetadata?.messageTemplateId,
		],
	);

	return {
		isPoMessageVisible,
		messageInstanceId,
		messageMetadata,
		PostOfficeMessageInstance,
		dismiss,
		renderJiraSpotlight,
	};
};
